import { isRejected, Middleware } from '@reduxjs/toolkit';
import { jobsActions } from './jobs';

// Mutation thunks of the annotation and comment slices ('annotations/add',
// 'comment/update', ...). Reads (get/delta) are excluded — they fail
// routinely while offline and have their own fallbacks.
const SAVE_REJECTED_TYPE = /^(annotations|comment)\/(add|update|delete)\/rejected$/;

/**
 * Surfaces rejected annotation/comment mutations as a centered toast via the
 * jobs system, in one place instead of a `.unwrap().catch()` per dispatch
 * site. Mutation thunks only reject when the server refuses the change
 * (e.g. the profanity filter) — network failures resolve as fulfilled
 * through the offline queue — so every rejection here is user-relevant.
 *
 * Component-level `.unwrap()` handlers (e.g. useCUDChangeManager's revert)
 * remain responsible for per-entity state repair only; user notification
 * happens once, here.
 */
export const saveErrorToastMiddleware: Middleware =
  (store) => (next) => (action) => {
    if (
      isRejected(action) &&
      !action.meta.aborted &&
      !action.meta.condition &&
      SAVE_REJECTED_TYPE.test(action.type)
    ) {
      const subject = action.type.startsWith('comment')
        ? 'Comment'
        : 'Annotation';
      const reason = action.error?.message || null;
      store.dispatch(
        jobsActions.addJob({
          id: `save-error-${action.meta.requestId}`,
          description: reason
            ? `${subject} not saved: ${reason}`
            : `${subject} could not be saved`,
        })
      );
    }
    return next(action);
  };
